import React, { useState, useEffect } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { UserPlus, Users } from "lucide-react";
import EditUserModal from "../../components/modals/EditUserModal";
import ResetPasswordModal from "../../components/modals/ResetPasswordModal";

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
    company: "",
  });
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const openEditUser = (user) => setEditUser(user);
  const openResetPassword = (user) => setResetUser(user);
  const closeModals = () => {
    setEditUser(null);
    setResetUser(null);
  };

  const fetchUsers = async () => {
    try {
      const res = await API.get("/auth/admins");
      setUsers(res.data?.data?.data ?? res.data?.data ?? []);
    } catch {
      toast.error("Error al obtener usuarios");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.warn("Completa todos los campos obligatorios");
      return;
    }

    try {
      setLoading(true);
      await API.post("/auth/register", form);
      toast.success("Usuario creado correctamente");
      setForm({
        name: "",
        email: "",
        password: "",
        role: "company",
      });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error al crear usuario");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          Usuarios del sistema
        </h2>
        <p className="text-gray-600">Administra los usuarios del sistema.</p>
      </div>

      <form
        onSubmit={createUser}
        className="
    bg-white shadow-md rounded-2xl p-6 mb-8 
    grid gap-4 
    grid-cols-1 
    sm:grid-cols-2 
    lg:grid-cols-3
  "
      >
        <div className="flex flex-col w-full">
          <input
            placeholder="Nombre"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-gray-300 rounded-xl p-3 
                 focus:outline-none focus:ring-2 focus:ring-supernova-400 w-full"
          />
        </div>

        <div className="flex flex-col w-full">
          <input
            placeholder="Correo electrónico"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="border border-gray-300 rounded-xl p-3 
                 focus:outline-none focus:ring-2 focus:ring-supernova-400 w-full"
          />
        </div>

        <div className="flex flex-col w-full">
          <input
            placeholder="Contraseña"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="border border-gray-300 rounded-xl p-3 
                 focus:outline-none focus:ring-2 focus:ring-supernova-400 w-full"
          />
        </div>

        <div className="col-span-1 sm:col-span-2 lg:col-span-1 w-full">
          <button
            disabled={loading}
            className="
        w-full bg-supernova-500 hover:bg-supernova-600 
        text-white rounded-xl py-3 flex justify-center 
        items-center gap-2 transition-all duration-200
      "
          >
            <UserPlus size={18} />
            {loading ? "Creando..." : "Crear usuario"}
          </button>
        </div>
      </form>

      <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <Users size={22} /> Usuarios registrados
      </h3>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {users.map((u, i) => (
          <motion.div
            key={u._id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="bg-white p-5 rounded-2xl shadow hover:shadow-lg transition-all duration-200"
          >
            <h4 className="font-semibold text-gray-800">{u.name}</h4>
            <p className="text-sm text-gray-500">{u.email}</p>

            <p className="text-xs mt-1">
              <span className="px-2 py-1 bg-gray-100 rounded-lg text-gray-600">
                {u.role}
              </span>
            </p>

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => openEditUser(u)}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm"
              >
                Editar
              </button>

              <button
                onClick={() => openResetPassword(u)}
                className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm"
              >
                Reset clave
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {users.length === 0 && (
        <p className="text-gray-500 text-center mt-6">
          No hay usuarios registrados aún.
        </p>
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={closeModals}
          onUpdated={fetchUsers}
        />
      )}

      {resetUser && (
        <ResetPasswordModal user={resetUser} onClose={closeModals} />
      )}
    </div>
  );
}
